import React, { useMemo } from "react";
import Plot from "react-plotly.js";

const formatPValue = (p) => {
  if (p === null || p === undefined || Number.isNaN(p)) return "-";
  if (p < 1e-4) return "< 1e-4";
  return p.toFixed(4);
};

const buildHistogram = (values, binCount = 30) => {
  if (!Array.isArray(values) || values.length === 0) return [];
  const finiteValues = values.filter((v) => Number.isFinite(v));
  if (finiteValues.length === 0) return [];

  const min = Math.min(...finiteValues);
  const max = Math.max(...finiteValues);
  const span = max - min || 1;
  const bins = Math.max(1, binCount);

  const counts = new Array(bins).fill(0);
  finiteValues.forEach((v) => {
    let idx = Math.floor(((v - min) / span) * bins);
    if (idx >= bins) idx = bins - 1;
    if (idx < 0) idx = 0;
    counts[idx] += 1;
  });

  const binWidth = span / bins;
  return counts.map((count, i) => ({
    binStart: min + i * binWidth,
    binEnd: min + (i + 1) * binWidth,
    count,
  }));
};

const BootstrapHistogramPlotly = ({ nullSamples, observed, pValue }) => {
  const bins = useMemo(() => buildHistogram(nullSamples, 30), [nullSamples]);

  if (!Array.isArray(nullSamples) || !bins.length) {
    return <p className="text-muted small mb-0">No bootstrap samples.</p>;
  }

  const minX = bins[0].binStart;
  const maxX = bins[bins.length - 1].binEnd;
  const x = bins.map((b) => (b.binStart + b.binEnd) / 2);
  const y = bins.map((b) => b.count);
  const widths = bins.map((b) => b.binEnd - b.binStart);
  const colors = bins.map((b) =>
    b.binEnd >= observed ? "rgba(220,53,69,0.5)" : "#0d6efd"
  );

  const clampedObserved = Math.min(Math.max(observed, minX), maxX);

  const data = [
    {
      type: "bar",
      x,
      y,
      width: widths,
      marker: { color: colors },
      hovertemplate: "D*: %{x}<br>Count: %{y}<extra></extra>",
    },
  ];

  const shapes = [
    {
      type: "line",
      x0: clampedObserved,
      x1: clampedObserved,
      y0: 0,
      y1: 1,
      xref: "x",
      yref: "paper",
      line: { color: "#dc3545", width: 2 },
    },
  ];

  const layout = {
    margin: { l: 40, r: 10, t: 10, b: 40 },
    xaxis: { title: "Distance", range: [minX, maxX] },
    yaxis: { title: "Count" },
    shapes,
    barmode: "overlay",
    bargap: 0,
  };

  return (
    <>
      <Plot
        data={data}
        layout={layout}
        style={{ width: "100%", height: 220 }}
        useResizeHandler
        config={{ displayModeBar: false }}
      />
      <div className="small text-muted mt-1">
        Observed distance: {observed.toFixed(4)}; p-value ≈ {formatPValue(pValue)}
      </div>
    </>
  );
};

export default BootstrapHistogramPlotly;

